import React, { useState, useEffect, useRef } from 'react';
import './Sidebar.css';

function SidebarPL({ activeSection, selectedCase, useCasesRef }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const sidebarRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (isMenuOpen && 
          sidebarRef.current && 
          !sidebarRef.current.contains(event.target) &&
          buttonRef.current &&
          !buttonRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  // If activeSection is "usecases" or one of the sub-IDs, we consider ourselves in Use Cases
  const isInUseCases = activeSection && [
    'usecases',
    // DevTools
    'devtools-overview',
    'devtools-specification',
    'devtools-problem',
    'devtools-process',
    'devtools-solution',
    'devtools-designfeatures',
    'devtools-conclusion',
    // DesktopApp
    'desktopapp-overview',
    'desktopapp-specification',
    'desktopapp-problem',
    'desktopapp-process',
    'desktopapp-solution',
    'desktopapp-conclusion',
    // MobileApp
    'mobileapp-overview',
    'mobileapp-specification',
    'mobileapp-problem',
    'mobileapp-process',
    'mobileapp-solution',
    'mobileapp-designfeatures',
    'mobileapp-conclusion'
  ].includes(activeSection);

  // We're in DevTools if activeSection starts with 'devtools-'
  const isInDevTools = activeSection && (activeSection.startsWith('devtools-') || selectedCase === 'devtools');
  // Similarly for DesktopApp, MobileApp
  const isInDesktopApp = activeSection && (activeSection.startsWith('desktopapp-') || selectedCase === 'desktopapp');
  const isInMobileApp = activeSection && (activeSection.startsWith('mobileapp-') || selectedCase === 'mobileapp');

  // Helper function to determine if a section is active
  const isSectionActive = (sectionId) => {
    if (!activeSection) return false;
    // If we're in a use case section, the Use Cases parent should be active
    if (sectionId === 'usecases' && isInUseCases) {
      return true;
    }
    // For other sections, check if the activeSection matches or starts with the sectionId
    return activeSection === sectionId || activeSection.startsWith(sectionId + '-');
  };

  // Handle use case selection
  const handleUseCaseSelect = (caseId) => {
    if (useCasesRef.current) {
      useCasesRef.current.selectUseCase(caseId);
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <>
      <button ref={buttonRef} className="mobile-menu-toggle" onClick={toggleMenu}>
        {isMenuOpen ? '✕' : '☰'}
      </button>
      <aside ref={sidebarRef} className={`sidebar ${isMenuOpen ? 'active' : ''}`}>
        {/* Level 1: Main Navigation */}
        <ul>
          <li className={isSectionActive('overview') ? 'active' : ''}>
            <a href="#overview" onClick={() => setIsMenuOpen(false)}>Przegląd</a>
          </li>
          <li className={isSectionActive('myrole') ? 'active' : ''}>
            <a href="#myrole" onClick={() => setIsMenuOpen(false)}>Moja rola</a>
          </li>
          <li className={isSectionActive('myuxprocess') ? 'active' : ''}>
            <a href="#myuxprocess" onClick={() => setIsMenuOpen(false)}>Mój proces UX</a>
          </li>

          {/* Level 2: Use Cases */}
          <li className={isSectionActive('usecases') ? 'active' : ''}>
            <a href="#usecases" onClick={() => setIsMenuOpen(false)}>Przypadki użycia</a>

            {/* Level 3: Individual Use Cases */}
            {isInUseCases && (
              <ul style={{ marginLeft: '1rem', marginTop: '0.5rem' }}>
                {/* Development Tools */}
                <li className={isInDevTools ? 'active' : ''}>
                  <a 
                    href="#devtools-overview" 
                    style={{ color: isInDevTools ? '#007bff' : '#999' }}
                    onClick={(e) => {
                      e.preventDefault();
                      handleUseCaseSelect('devtools');
                      setIsMenuOpen(false);
                    }}
                  >
                    Development Tools
                  </a>
                  {/* Level 4: Development Tools Sections */}
                  {isInDevTools && (
                    <ul style={{ marginLeft: '1rem', marginTop: '0.5rem' }}>
                      <li className={isSectionActive('devtools-overview') ? 'active' : ''}>
                        <a href="#devtools-overview">Przegląd</a>
                      </li>
                      <li className={isSectionActive('devtools-specification') ? 'active' : ''}>
                        <a href="#devtools-specification">Specyfikacja</a>
                      </li>
                      <li className={isSectionActive('devtools-problem') ? 'active' : ''}>
                        <a href="#devtools-problem">Problem</a>
                      </li>
                      <li className={isSectionActive('devtools-process') ? 'active' : ''}>
                        <a href="#devtools-process">Proces</a>
                      </li>
                      <li className={isSectionActive('devtools-solution') ? 'active' : ''}>
                        <a href="#devtools-solution">Rozwiązanie</a>
                      </li>
                      <li className={isSectionActive('devtools-designfeatures') ? 'active' : ''}>
                        <a href="#devtools-designfeatures">Funkcje projektowe</a>
                      </li>
                      <li className={isSectionActive('devtools-conclusion') ? 'active' : ''}>
                        <a href="#devtools-conclusion">Podsumowanie</a>
                      </li>
                    </ul>
                  )}
                </li>

                {/* Desktop App */}
                <li className={isInDesktopApp ? 'active' : ''}>
                  <a 
                    href="#desktopapp-overview" 
                    style={{ color: isInDesktopApp ? '#007bff' : '#999' }}
                    onClick={(e) => {
                      e.preventDefault();
                      handleUseCaseSelect('desktopapp');
                      setIsMenuOpen(false);
                    }}
                  >
                    Aplikacja Desktopowa
                  </a>
                  {/* Level 4: Desktop App Sections */}
                  {isInDesktopApp && (
                    <ul style={{ marginLeft: '1rem', marginTop: '0.5rem' }}>
                      <li className={isSectionActive('desktopapp-overview') ? 'active' : ''}>
                        <a href="#desktopapp-overview">Przegląd</a>
                      </li>
                      <li className={isSectionActive('desktopapp-specification') ? 'active' : ''}>
                        <a href="#desktopapp-specification">Specyfikacja</a>
                      </li>
                      <li className={isSectionActive('desktopapp-problem') ? 'active' : ''}>
                        <a href="#desktopapp-problem">Wyzwanie</a>
                      </li>
                      <li className={isSectionActive('desktopapp-process') ? 'active' : ''}>
                        <a href="#desktopapp-process">Proces</a>
                      </li>
                      <li className={isSectionActive('desktopapp-solution') ? 'active' : ''}>
                        <a href="#desktopapp-solution">Ideacja rozwiązania</a>
                      </li>
                      <li className={isSectionActive('desktopapp-conclusion') ? 'active' : ''}>
                        <a href="#desktopapp-conclusion">Podsumowanie</a>
                      </li>
                    </ul>
                  )}
                </li>

                {/* Mobile App */}
                <li className={isInMobileApp ? 'active' : ''}>
                  <a 
                    href="#mobileapp-overview" 
                    style={{ color: isInMobileApp ? '#007bff' : '#999' }}
                    onClick={(e) => {
                      e.preventDefault();
                      handleUseCaseSelect('mobileapp');
                      setIsMenuOpen(false);
                    }}
                  >
                    Aplikacja Mobilna
                  </a>
                  {/* Level 4: Mobile App Sections */}
                  {isInMobileApp && (
                    <ul style={{ marginLeft: '1rem', marginTop: '0.5rem' }}>
                      <li className={isSectionActive('mobileapp-overview') ? 'active' : ''}>
                        <a href="#mobileapp-overview">Przegląd</a>
                      </li>
                      <li className={isSectionActive('mobileapp-specification') ? 'active' : ''}>
                        <a href="#mobileapp-specification">Specyfikacja</a>
                      </li>
                      <li className={isSectionActive('mobileapp-problem') ? 'active' : ''}>
                        <a href="#mobileapp-problem">Wyzwanie</a>
                      </li>
                      <li className={isSectionActive('mobileapp-process') ? 'active' : ''}>
                        <a href="#mobileapp-process">Proces</a>
                      </li>
                      <li className={isSectionActive('mobileapp-solution') ? 'active' : ''}>
                        <a href="#mobileapp-solution">Ideacja rozwiązania</a>
                      </li>
                      <li className={isSectionActive('mobileapp-designfeatures') ? 'active' : ''}>
                        <a href="#mobileapp-designfeatures">Funkcje projektowe</a>
                      </li>
                      <li className={isSectionActive('mobileapp-conclusion') ? 'active' : ''}>
                        <a href="#mobileapp-conclusion">Podsumowanie</a>
                      </li>
                    </ul>
                  )}
                </li>
              </ul>
            )}
          </li>
        </ul>
      </aside>
    </>
  );
}

export default SidebarPL; 